import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { User } from "oidc-client-ts";
import { userManager, principalFromUser, type Principal } from "./oidc";
import { config } from "../models/config";

interface AuthState {
  status: "loading" | "authenticated" | "unauthenticated";
  user: User | null;
  principal: Principal | null;
  login: () => void;
  logout: () => void;
  /** Current bearer token, or null. Used by the transport to authorize BFF calls. */
  token: () => string | null;
}

const AuthCtx = createContext<AuthState | null>(null);

// The shell has NO login UI of its own. In every mode, an unauthenticated visitor is redirected
// straight to the identity provider (Keycloak, directly or via the edge oauth2-proxy); we never
// render a compath login/landing screen.
//
// Two auth modes (picked at load from /env.js, stable for the session):
//   - "oidc": the shell drives Authorization Code + PKCE against Keycloak/compath-auth (local dev).
//   - "edge": an edge oauth2-proxy already authenticated the request and injects the bearer; the
//             shell only reads identity from /api/me and never runs OIDC.
export function AuthProvider({ children }: { children: React.ReactNode }) {
  if (config.authMode === "dev") return <DevAuthProvider>{children}</DevAuthProvider>;
  if (config.authMode === "edge") return <EdgeAuthProvider>{children}</EdgeAuthProvider>;
  return <OidcAuthProvider>{children}</OidcAuthProvider>;
}

// ── dev mode: local BYPASS, no login ─────────────────────────────────────────────────────────
// The backend (compath.dev.auth.enabled) injects a fixed demo identity for every request, so the
// shell never touches OIDC and sends no bearer. It just reads identity from /api/me (the X-Dev-User
// header, added by apiClient, selects the persona). Groups aren't on /api/me, so admin-ness is
// derived locally from the known persona (only "hong" is the platform admin).
const DEV_ADMIN_USERS = new Set(["hong"]);

function DevAuthProvider({ children }: { children: React.ReactNode }) {
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");

  useEffect(() => {
    let live = true;
    (async () => {
      const groups = DEV_ADMIN_USERS.has(config.devUser) ? ["sp-admin"] : ["sp-dev"];
      try {
        const res = await fetch(`${config.apiBase}/api/me`, {
          credentials: "same-origin",
          headers: { "X-Dev-User": config.devUser },
        });
        const me = res.ok
          ? ((await res.json()) as { id?: string; displayName?: string; name?: string })
          : {};
        if (!live) return;
        setPrincipal({
          id: String(me.id ?? config.devUser),
          name: String(me.displayName ?? me.name ?? config.devUser),
          groups,
        });
      } catch {
        // Even if /api/me is briefly unavailable, dev mode never blocks on a login screen.
        if (live) setPrincipal({ id: config.devUser, name: config.devUser, groups });
      } finally {
        if (live) setStatus("authenticated");
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user: null,
      principal,
      login: () => {},
      logout: () => {},
      token: () => null,
    }),
    [status, principal],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

// ── edge mode: identity from /api/me, login/logout via the edge proxy ────────────────────────
function edgeStartLogin(): void {
  const rd = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/oauth2/start?rd=${rd}`;
}

// Ends the edge proxy session AND the Keycloak SSO session, then lands back on the app (which
// immediately bounces to the Keycloak login). Passing client_id + post_logout_redirect_uri makes
// Keycloak skip its "Do you want to log out?" confirmation page and redirect straight back.
function edgeLogoutUrl(): string {
  const kcLogout =
    `${config.oidcAuthority.replace(/\/$/, "")}/protocol/openid-connect/logout` +
    `?client_id=${encodeURIComponent(config.oidcClientId)}` +
    `&post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
  return `/oauth2/sign_out?rd=${encodeURIComponent(kcLogout)}`;
}

function EdgeAuthProvider({ children }: { children: React.ReactNode }) {
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(`${config.apiBase}/api/me`, { credentials: "same-origin" });
        if (!live) return;
        if (res.ok) {
          const me = (await res.json()) as { id?: string; name?: string; groups?: string[] };
          setPrincipal({
            id: String(me.id ?? ""),
            name: String(me.name ?? "사용자"),
            groups: Array.isArray(me.groups) ? me.groups : [],
          });
          setStatus("authenticated");
        } else {
          // Not authenticated (401) or any other non-OK: never show a compath screen — go to the
          // edge sign-in, which drives the Keycloak login.
          edgeStartLogin();
        }
      } catch {
        if (live) edgeStartLogin();
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user: null,
      principal,
      login: edgeStartLogin,
      logout: () => {
        window.location.href = edgeLogoutUrl();
      },
      token: () => null,
    }),
    [status, principal],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

// ── oidc mode: the shell owns the PKCE flow ──────────────────────────────────────────────────
// StrictMode (dev) mounts effects twice; without a guard the authorization code would be exchanged
// twice and the second exchange fails ("code already used"), bouncing the user into a login loop. Memo
// the single in-flight callback so both invocations await the SAME exchange.
let callbackExchange: Promise<User> | null = null;
// While a logout is navigating to Keycloak's end-session endpoint, signoutRedirect first unloads the
// stored user — which flips status to "unauthenticated" and would race the auto-signin effect below,
// bouncing the browser to the login (SSO still live) and back into the app. This flag suppresses that.
let loggingOut = false;

function OidcAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");

  useEffect(() => {
    let live = true;
    async function boot() {
      try {
        // Returning from Keycloak with ?code=...: finish the code exchange, then clean the URL.
        if (window.location.pathname === "/callback") {
          callbackExchange = callbackExchange ?? userManager.signinRedirectCallback();
          const u = await callbackExchange;
          if (!live) return;
          window.history.replaceState({}, "", "/");
          setUser(u);
          setStatus("authenticated");
          return;
        }
        const u = await userManager.getUser();
        if (!live) return;
        if (u && !u.expired) {
          setUser(u);
          setStatus("authenticated");
        } else {
          setStatus("unauthenticated");
        }
      } catch {
        if (live) setStatus("unauthenticated");
      }
    }
    void boot();

    const onLoaded = (u: User) => setUser(u);
    const onUnloaded = () => { setUser(null); setStatus("unauthenticated"); };
    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addUserUnloaded(onUnloaded);
    return () => {
      live = false;
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeUserUnloaded(onUnloaded);
    };
  }, []);

  // No compath login screen: as soon as we know we are unauthenticated, bounce to Keycloak.
  // This also means "logout → land unauthenticated → Keycloak login" happens automatically.
  useEffect(() => {
    if (status === "unauthenticated" && !loggingOut) {
      void userManager.signinRedirect();
    }
  }, [status]);

  const value = useMemo<AuthState>(() => ({
    status,
    user,
    principal: user ? principalFromUser(user) : null,
    login: () => void userManager.signinRedirect(),
    // signoutRedirect sends id_token_hint + post_logout_redirect_uri, so Keycloak logs out without
    // its confirmation page and redirects back to the login. `loggingOut` blocks the auto-signin race.
    logout: () => {
      loggingOut = true;
      void userManager.signoutRedirect().catch(() => {
        loggingOut = false;
        void userManager.removeUser();
      });
    },
    token: () => user?.access_token ?? null,
  }), [status, user]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const c = useContext(AuthCtx);
  if (!c) throw new Error("useAuth must be used inside <AuthProvider>");
  return c;
}
